import React, { useState } from "react";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { HiOutlinePencilAlt } from "react-icons/hi";
import {
	getAllDepartments,
	getUserTypes,
	updateUserById,
} from "@/services/auth-functions";
import { EditUserByIdParams, UserSystem } from "@/context/User/UserContext";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../ui/command";
import { cn } from "@/lib/utils";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { STALE_TIME } from "@/constants";

const formSchema = z.object({
	department: z.number(),
	usertypeid: z.number(),
});

const EditUser: React.FC<{
	user: UserSystem;
	accessToken: string;
}> = ({ user, accessToken }) => {
	const [open, setOpen] = useState(false);
	const [departmentOpen, setDepartmentOpen] = useState(false);

	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const departments = useQuery({
		queryKey: ["all-departments"],
		queryFn: () => getAllDepartments(accessToken),
		staleTime: STALE_TIME,
	});

	const usertypes = useQuery({
		queryKey: ["all-usertypes"],
		queryFn: () => getUserTypes(accessToken),
		staleTime: STALE_TIME,
	});

	const updateUser = useMutation({
		mutationKey: ["update-user", user.id],
		mutationFn: (details: EditUserByIdParams) =>
			updateUserById(accessToken, details),
		onSuccess: () => {
			queryClient.invalidateQueries(["user-detail", user.id]);
			toast.success(`${user.username} details updated`, {
				description: `The details of ${user.username} have been updated`,
			});
			setOpen(false);
			form.reset();
			updateUser.reset();
			navigate("/allusers");
		},
		onError: () => {
			toast.error("Error editing user");
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		updateUser.mutate({
			department: data.department,
			usertypeid: data.usertypeid,
			userid: user.id,
		});
	};

	const disabled = () => {
		if (updateUser.isLoading) return true;
		if (updateUser.isSuccess) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={(open) => setOpen(open)}>
			<div className="flex gap-2">
				<DialogTrigger asChild>
					<Button>Edit User</Button>
				</DialogTrigger>
			</div>
			<DialogContent className="sm:max-w-md">
				<DialogHeader className="text-left">
					<DialogTitle className="text-primary flex gap-2 items-center mb-1">
						<div className="bg-primary/20 p-1 rounded-full">
							<HiOutlinePencilAlt className="text-xl" />
						</div>
						Edit {user.username}
					</DialogTitle>
					<DialogDescription>
						Update the department and user type below.
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="flex items-center space-x-2 mb-4">
							<div className="grid flex-1 gap-2">
								{departments.isLoading ? (
									<span>Loading GL Accounts</span>
								) : departments.data ? (
									<FormField
										name="department"
										render={({ field }) => (
											<FormItem className="flex flex-col">
												<FormLabel>Department</FormLabel>
												<Popover
													open={departmentOpen}
													onOpenChange={(open) => setDepartmentOpen(open)}
												>
													<PopoverTrigger asChild>
														<FormControl>
															<Button
																variant="outline"
																role="combobox"
																className={cn(
																	"justify-between font-normal",
																	!field.value && "text-muted-foreground"
																)}
																disabled={disabled()}
															>
																{field.value
																	? departments.data.find(
																			(department) => department.departmentid === field.value
																	  )?.departmentname
																	: "Select department"}
																<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
															</Button>
														</FormControl>
													</PopoverTrigger>
													<PopoverContent className="p-0 w-[calc(100vw-2rem)] sm:w-[400px]">
														<Command>
															<CommandInput placeholder="Search department..." />
															<CommandList>
																<CommandEmpty>No account found.</CommandEmpty>
																<CommandGroup>
																	{departments.data.map((item) => (
																		<CommandItem
																			value={item.departmentname}
																			key={item.departmentid.toString()}
																			onSelect={() => {
																				form.setValue("department", item.departmentid);
																				setDepartmentOpen(false);
																			}}
																		>
																			<Check
																				className={cn(
																					"mr-2 h-4 w-4",
																					item.departmentid.toString() === field.value
																						? "opacity-100"
																						: "opacity-0"
																				)}
																			/>
																			{item.departmentname}
																		</CommandItem>
																	))}
																</CommandGroup>
															</CommandList>
														</Command>
													</PopoverContent>
												</Popover>
												<FormMessage />
											</FormItem>
										)}
									/>
								) : null}
								{usertypes.isLoading ? (
									<span>Loading GL Accounts</span>
								) : usertypes.data ? (
									<FormField
										name="usertypeid"
										render={({ field }) => (
											<FormItem className="mb-2">
												<FormLabel>User Type</FormLabel>
												<FormControl>
													<Select
														onValueChange={(e) => {
															field.onChange(parseFloat(e));
														}}
														disabled={disabled()}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select an option" />
														</SelectTrigger>
														<SelectContent>
															{usertypes.data.map((item) => (
																<SelectItem value={item.usertypeid.toString()}>
																	{item.usertype}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								) : null}
							</div>
						</div>

						<DialogFooter className="sm:justify-end">
							<DialogClose asChild>
								<Button type="button" variant="ghost">
									Cancel
								</Button>
							</DialogClose>
							<Button type="submit" disabled={disabled()}>
								{disabled() ? "Submitting..." : "Confirm"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default EditUser;
